import first from "lodash/first";
import { type FunctionComponent, useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";

import { notEmpty } from "shared/lib/utils";

import { getVideoYoutubeId, getVideoYoutubeThumbnailUrl } from "scmp-app/components/video/helpers";
import { useVideoShortCarouselContext } from "scmp-app/components/video/video-short-carousel/contexts";
import type { videoShortCarouselCard$key } from "scmp-app/queries/__generated__/videoShortCarouselCard.graphql";

import { Container, Headline, StyledImage } from "./styles";

type Props = {
  className?: string;
  reference: videoShortCarouselCard$key;
  slideId: number;
};

export const VideoShortCarouselCard: FunctionComponent<Props> = ({
  className,
  reference,
  slideId,
}) => {
  const video = useFragment(
    graphql`
      fragment videoShortCarouselCard on Video {
        entityId
        headline
        socialHeadline
        youtubeId
        images {
          portraitStyle: style(filter: { style: "portrait" }) {
            url
          }
        }
        ...helpersGetVideoYoutubeIdVideo
      }
    `,
    reference,
  );
  const youtubeId = useMemo(() => getVideoYoutubeId(video), [video]);
  const isValid = notEmpty(video.entityId) && notEmpty(video.youtubeId);
  const { playVideoShort } = useVideoShortCarouselContext();

  const displayHeadline = findFirstNonEmptyString(video.socialHeadline, video.headline);

  const mainImageUrl = useMemo(() => {
    const youtubeThumb = first(getVideoYoutubeThumbnailUrl(youtubeId));
    if (notEmpty(youtubeThumb)) return youtubeThumb;
    return first(video?.images)?.portraitStyle?.url ?? "";
  }, [video, youtubeId]);

  const handleClick = useCallback(() => {
    if (!isValid) return;
    playVideoShort?.(video.entityId, slideId, video.youtubeId);
  }, [isValid, playVideoShort, slideId, video.entityId, video.youtubeId]);

  if (!isValid) return null;

  return (
    <Container className={className} onClick={handleClick}>
      <StyledImage $aspectRatio="200/356" alt={displayHeadline} lazyload src={mainImageUrl} />
      <Headline>{displayHeadline}</Headline>
    </Container>
  );
};

VideoShortCarouselCard.displayName = "VideoShortCarouselCard";
